import { environment } from '../../../../environments/environment';

/**
 * Determines the cookie domain based on the environment.
 * For localhost, no domain is used to allow cookies to be set on localhost.
 * For other environments, it uses the root domain (e.g., `.cantrak.tech`).
 */
const getCookieDomain = (): string | undefined => {
    const url = environment.appUrl || environment.appUrl2;
    const hostname = new URL(url).hostname;

    // Use root domain for non-localhost environments
    if (!hostname.includes('localhost')) {
        const domainParts = hostname.split('.').slice(-2);
        return `.${domainParts.join('.')}`;
    }

    // For localhost, return undefined to omit the domain attribute
    return undefined;
};

/**
 * Sets a cookie with a specified name, value, and expiration in days.
 * @param name - Name of the cookie.
 * @param value - Value of the cookie.
 * @param days - Expiration time in days.
 */
export const setCookie = (name: string, value: string, days: number): void => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

    const domain = getCookieDomain();
    let cookieString = `${name}=${value}; Expires=${date.toUTCString()}; Path=/; SameSite=Lax`;

    // Add the domain if specified and omit Secure flag for localhost
    if (domain) {
        cookieString += `; Domain=${domain}; Secure`;
    }

    originalConsoleLog(`Setting Cookie: `, cookieString); // Log the cookie string for verification

    document.cookie = cookieString;
};

/**
 * Retrieves the value of a cookie by name.
 * @param name - Name of the cookie to retrieve.
 * @returns The cookie value or null if not found.
 */
/**
 * Retrieves the value of a cookie by name and removes any surrounding quotes if present.
 * @param name - Name of the cookie to retrieve.
 * @returns The cookie value without surrounding quotes, or null if not found.
 */
export const getCookie = (name: string): string | null => {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
        cookie = cookie.trim();
        if (cookie.startsWith(nameEQ)) {
            let cVal = cookie.substring(nameEQ.length);

            // Remove any surrounding double quotes from the value
            if (cVal.startsWith('"') && cVal.endsWith('"')) {
                cVal = cVal.slice(1, -1);
            }

            originalConsoleLog(`Cookie with name: `, nameEQ, `, Value: `, cVal);
            return cVal;
        }
    }

    originalConsoleLog(`Cookie with name: `, nameEQ, `, Value: `, null);
    return null;
};


/**
 * Deletes a cookie by name.
 * @param name - Name of the cookie to delete.
 */
export const deleteCookie = (name: string): void => {
    const domain = getCookieDomain();
    let cookieString = `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 UTC; Path=/; SameSite=Lax`;

    if (domain) {
        cookieString += `; Domain=${domain}; Secure`;
    }

    originalConsoleLog(`Deleting Cookie: `, cookieString);

    document.cookie = cookieString;
};

/**
 * Logs messages using the original console.log if available.
 * @param args - Messages or variables to log.
 */
function originalConsoleLog(...args: any[]): void {
    if (typeof (window as any).__originalConsoleLog === 'function') {
        (window as any).__originalConsoleLog(...args);
    } else {
        console.log(...args); // Fallback to default console.log if original is not available
    }
}
